import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingAccessLogDto,
  MeetingShareCandidateDto,
  MeetingSharePersonDto,
  MeetingSharingDto,
} from '@interfaces/meetings/responses/meeting-sharing.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { Observable, filter, first } from 'rxjs';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';
import {
  ACESSOS_VISIVEIS,
  PRAZOS_DO_LINK,
  PrazoDoLink,
  iconeDaAcao,
  opcoesDeCandidatos,
  pedidoDoPrazo,
  rotuloDaAcao,
  rotuloDaVia,
  urlDoLink,
} from './meeting-sharing-state';

/**
 * Janela Compartilhar da meeting (card 3241), só para quem participou (`canShare`):
 *
 * - **Pessoas**: escolher gente do time interno; recebem sem prazo e veem como quem participou.
 * - **Link**: qualquer pessoa do time interno que abrir passa a ver até o prazo (7 dias por padrão,
 *   ou sem prazo). A URL aparece só logo depois de gerar (o backend guarda só o hash); revogar corta
 *   quem entrou por ele.
 * - **Acessos**: quem abriu, assistiu, baixou ou leu a transcrição, e por onde. Recolhido além de 5.
 *
 * O acesso geral (time inteiro) continua no botão do topo, com as regras do card 3191.
 */
@Component({
  selector: 'app-modal-share-meeting',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './modal-share-meeting.component.html',
  styleUrl: './modal-share-meeting.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalShareMeetingComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** A meeting mudou de acesso geral: a tela troca o topo pelo que voltou. */
  @Output() acessoGeralMudou = new EventEmitter<MeetingDto>();

  protected readonly ligacao = signal<MeetingDto | null>(null);
  protected readonly compartilhamento = signal<MeetingSharingDto | null>(null);
  protected readonly candidatos = signal<MeetingShareCandidateDto[]>([]);
  protected readonly acessos = signal<MeetingAccessLogDto[]>([]);
  protected readonly totalDeAcessos = signal(0);
  protected readonly carregando = signal(false);
  protected readonly gravando = signal(false);

  protected readonly escolhidas = signal<string[]>([]);
  protected readonly prazo = signal<PrazoDoLink>('7');
  /** URL do link recém-gerado: só existe nesta abertura da janela. */
  protected readonly urlGerada = signal<string | null>(null);
  protected readonly verTodosOsAcessos = signal(false);

  protected readonly prazos = PRAZOS_DO_LINK;
  protected readonly rotuloDaVia = rotuloDaVia;
  protected readonly rotuloDaAcao = rotuloDaAcao;
  protected readonly iconeDaAcao = iconeDaAcao;

  protected readonly opcoes = computed(() =>
    opcoesDeCandidatos(this.candidatos(), this.compartilhamento()?.people ?? [])
  );
  protected readonly acessosVisiveis = computed(() =>
    this.verTodosOsAcessos() ? this.acessos() : this.acessos().slice(0, ACESSOS_VISIVEIS)
  );
  protected readonly acessosOcultos = computed(() =>
    Math.max(0, this.acessos().length - ACESSOS_VISIVEIS)
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly confirmationService: ConfirmationService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  open(meeting: MeetingDto): void {
    this.ligacao.set(meeting);
    this.compartilhamento.set(null);
    this.acessos.set([]);
    this.totalDeAcessos.set(0);
    this.escolhidas.set([]);
    this.prazo.set('7');
    this.urlGerada.set(null);
    this.verTodosOsAcessos.set(false);
    this.carregar(meeting.id);
    this.dialogRef?.open('', '', '', 'Fechar', '', 'share-meeting', false, true);
  }

  isFormValid(): boolean {
    return !!this.ligacao() && !this.gravando() && this.escolhidas().length > 0;
  }

  /** Compartilha com as pessoas escolhidas. */
  submitAction(): void {
    const meeting = this.ligacao();
    if (!meeting || !this.isFormValid()) return;
    const quantas = this.escolhidas().length;
    this.gravar(this.meetingsService.shareMeeting(meeting.id, this.escolhidas()), () => {
      this.escolhidas.set([]);
      this.toast.showToastSuccess(
        quantas === 1 ? 'Meeting compartilhada.' : `Meeting compartilhada com ${quantas} pessoas.`
      );
    });
  }

  /** Libera para o time interno inteiro ou volta a privada (mesma regra do card 3191). */
  protected alternarAcessoGeral(): void {
    const meeting = this.ligacao();
    const atual = this.compartilhamento();
    if (!meeting || !atual) return;
    const liberar = !atual.isShared;
    this.confirmationService
      .confirm(
        liberar ? 'Liberar para o time da Legatus?' : 'Voltar a meeting a privada?',
        liberar
          ? 'Todo o time da Legatus passa a ver esta meeting, com o vídeo e a transcrição. Qualquer participante pode desfazer.'
          : 'Só quem participou e as pessoas com quem foi compartilhada continuam vendo.',
        liberar ? 'default' : 'danger',
        undefined,
        liberar ? 'Liberar' : 'Voltar a privada'
      )
      .pipe(
        first(),
        filter(ok => ok)
      )
      .subscribe(() => {
        this.gravando.set(true);
        this.meetingsService
          .setGeneralAccess(meeting.id, liberar)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: atualizada => {
              this.gravando.set(false);
              this.compartilhamento.set({ ...atual, isShared: liberar });
              if (atualizada) {
                this.ligacao.set(atualizada);
                this.acessoGeralMudou.emit(atualizada);
              }
              this.toast.showToastSuccess(
                liberar ? 'Meeting liberada para o time da Legatus.' : 'Meeting privada de novo.'
              );
            },
            error: erro => this.falhou(erro),
          });
      });
  }

  protected tirarAcesso(pessoa: MeetingSharePersonDto): void {
    const meeting = this.ligacao();
    if (!meeting) return;
    this.confirmationService
      .confirm(
        `Tirar o acesso de ${pessoa.name}?`,
        'A pessoa deixa de ver esta meeting na hora. Dá para compartilhar de novo depois.',
        'danger',
        undefined,
        'Tirar acesso'
      )
      .pipe(
        first(),
        filter(ok => ok)
      )
      .subscribe(() =>
        this.gravar(this.meetingsService.unshareMeeting(meeting.id, pessoa.userId), () =>
          this.toast.showToastSuccess('Acesso retirado.')
        )
      );
  }

  protected gerarLink(): void {
    const meeting = this.ligacao();
    if (!meeting || this.gravando()) return;
    const { expiresInDays, noExpiration } = pedidoDoPrazo(this.prazo());
    this.gravando.set(true);
    this.meetingsService
      .createShareLink(meeting.id, expiresInDays, noExpiration)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: criado => {
          this.gravando.set(false);
          if (!criado) return;
          const url = urlDoLink(this.document.location?.origin ?? '', criado.token);
          this.urlGerada.set(url);
          this.copiar(url);
          this.carregar(meeting.id, false);
        },
        error: erro => this.falhou(erro),
      });
  }

  protected revogarLink(): void {
    const meeting = this.ligacao();
    const link = this.compartilhamento()?.activeLink;
    if (!meeting || !link) return;
    this.confirmationService
      .confirm(
        'Revogar o link?',
        link.activeUsers
          ? `O link para de abrir e ${link.activeUsers === 1 ? 'a pessoa que entrou por ele perde' : `as ${link.activeUsers} pessoas que entraram por ele perdem`} o acesso na hora.`
          : 'O link para de abrir.',
        'danger',
        undefined,
        'Revogar'
      )
      .pipe(
        first(),
        filter(ok => ok)
      )
      .subscribe(() =>
        this.gravar(this.meetingsService.revokeShareLink(meeting.id, link.id), () => {
          this.urlGerada.set(null);
          this.toast.showToastSuccess('Link revogado.');
        })
      );
  }

  protected copiar(url: string): void {
    const clipboard = this.document.defaultView?.navigator?.clipboard;
    if (!clipboard) return;
    clipboard
      .writeText(url)
      .then(() => this.toast.showToastSuccess('Link copiado.'))
      .catch(() => undefined);
  }

  fechar(): void {
    this.dialogRef?.close();
  }

  /** Compartilhamento, candidatos e acessos da reunião. */
  private carregar(meetingId: string, comCandidatos = true): void {
    this.carregando.set(true);
    this.meetingsService
      .getSharing(meetingId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: dados => {
          this.carregando.set(false);
          this.compartilhamento.set(dados);
        },
        error: erro => {
          this.carregando.set(false);
          this.toast.showToastError(
            'Não foi possível carregar o compartilhamento',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });

    if (comCandidatos)
      this.meetingsService
        .getShareCandidates(meetingId)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe({ next: lista => this.candidatos.set(lista ?? []), error: () => undefined });

    this.meetingsService
      .getAccesses(meetingId, 1, 100)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: pagina => {
          this.acessos.set(pagina?.data ?? []);
          this.totalDeAcessos.set(pagina?.total ?? 0);
        },
        error: () => undefined,
      });
  }

  /** Grava e troca o estado da janela pelo que o backend devolveu. */
  private gravar(chamada: Observable<MeetingSharingDto | null>, sucesso: () => void): void {
    this.gravando.set(true);
    chamada.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: dados => {
        this.gravando.set(false);
        if (dados) this.compartilhamento.set(dados);
        sucesso();
      },
      error: erro => this.falhou(erro),
    });
  }

  private falhou(erro: unknown): void {
    this.gravando.set(false);
    this.toast.showToastError(
      'Não foi possível concluir',
      mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
    );
  }
}
